import React, { useState, useEffect } from 'react';
import { lookupsApi, manualTradingApi, mlApi } from '../../services/api';
import { Instrument, Resolution, ConsoleLogMessage, TRADING_ACCOUNTS } from '../../types';
import ResultModal from '../../components/common/ResultModal';
import TradeModal from '../../components/TradeModal/TradeModal';
import ConsoleLog from '../../components/ConsoleLog/ConsoleLog';
import { sseService } from '../../services/sseService';
import './ManualTrading.css';

const ManualTrading: React.FC = () => {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);

  const [exitReason, setExitReason] = useState('');
  const [size, setSize] = useState(1);
  const [loading, setLoading] = useState(false);

  const [currentTradeId, setCurrentTradeId] = useState<string | null>(null);
  const [isActiveTradeManual, setIsActiveTradeManual] = useState<boolean>(false);
  const [entryPrice, setEntryPrice] = useState<number | null>(null);
  const [entryTime, setEntryTime] = useState<number | null>(null);
  const [activeTradeInstrument, setActiveTradeInstrument] = useState<string>('');
  const [activeTradeResolution, setActiveTradeResolution] = useState<string>('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalVariant, setModalVariant] = useState<'success' | 'error' | 'warning'>('success');
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState<any>(null);

  // Trade modal state
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeModalType, setTradeModalType] = useState<'BUY' | 'SELL' | 'EXIT'>('BUY');

  // Console logs state
  const [consoleLogs, setConsoleLogs] = useState<ConsoleLogMessage[]>([]);

  const entryReasons = [
    { value: 'confirmed_bounce_long', label: 'Confirmed Bounce Long' },
    { value: 'rsi_oversold_confirmed', label: 'RSI Oversold Confirmed' },
    { value: 'confirmed_rejection_short', label: 'Confirmed Rejection Short' },
    { value: 'rsi_overbought_confirmed', label: 'RSI Overbought Confirmed' },
  ];

  const exitReasons = [
    { value: 'manual_close', label: 'Manual Close' },
    { value: 'quick_profit', label: 'Quick Profit' },
    { value: 'breakeven_stop', label: 'Breakeven Stop' },
    { value: 'max_loss_hit', label: 'Max Loss Hit' },
  ];

  const [openTrades, setOpenTrades] = useState<any[]>([]);

  // ML Prediction state
  const [h1MfePrediction, setH1MfePrediction] = useState<any>(null);
  const [reversalPrediction, setReversalPrediction] = useState<any>(null);
  const [mlLoading, setMlLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadOpenTradeStatus();

    // Load active trade data from localStorage on mount
    const storedTradeId = localStorage.getItem('manualTradingActiveTradeId');
    const storedInstrument = localStorage.getItem('manualTradingActiveInstrument');
    const storedResolution = localStorage.getItem('manualTradingActiveResolution');
    const storedIsManual = localStorage.getItem('manualTradingIsActiveTradeManual');

    if (storedTradeId) setCurrentTradeId(storedTradeId);
    if (storedInstrument) setActiveTradeInstrument(storedInstrument);
    if (storedResolution) setActiveTradeResolution(storedResolution);
    if (storedIsManual) setIsActiveTradeManual(storedIsManual === 'true');

    // Connect to console stream for backend logs
    sseService.connectConsoleStream((log: ConsoleLogMessage) => {
      setConsoleLogs((prevLogs) => [...prevLogs, log]);
    });

    // Connect to trade events for real-time updates
    sseService.connectTradeEvents(() => {
      localStorage.removeItem('manualTradingActiveTradeId');
      localStorage.removeItem('manualTradingActiveInstrument');
      localStorage.removeItem('manualTradingActiveResolution');
      localStorage.removeItem('manualTradingIsActiveTradeManual');
      setCurrentTradeId(null);
      setActiveTradeInstrument('');
      setActiveTradeResolution('');
      setIsActiveTradeManual(false);
      loadOpenTradeStatus();
    });

    return () => {
      sseService.disconnect();
    };
  }, []);

  const loadData = async () => {
    try {
      const [insts, ress] = await Promise.all([
        lookupsApi.getInstruments(),
        lookupsApi.getResolutions()
      ]);
      setInstruments(insts);
      setResolutions(ress);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadOpenTradeStatus = async () => {
    try {
      const allTrades = await manualTradingApi.getTrades();
      let foundOpenTrade = false;

      if (allTrades && allTrades.length > 0) {
        allTrades.forEach((trade: any) => {
          if (trade.status === 'OPEN') {
            foundOpenTrade = true;
            setCurrentTradeId(trade.tradingview_trade_id || trade.trade_id?.toString());
            setActiveTradeInstrument(trade.instrument);
            setActiveTradeResolution(trade.timeframe);

            const openTradeAccountId = trade.account_id || trade.accountId || '';
            const isManual = openTradeAccountId === TRADING_ACCOUNTS.MANUAL;
            setIsActiveTradeManual(isManual);

            if (trade.tradingview_trade_id || trade.trade_id) {
              localStorage.setItem('manualTradingActiveTradeId', trade.tradingview_trade_id || trade.trade_id.toString());
              localStorage.setItem('manualTradingActiveInstrument', trade.instrument);
              localStorage.setItem('manualTradingActiveResolution', trade.timeframe);
              localStorage.setItem('manualTradingIsActiveTradeManual', isManual.toString());
            }
          }
        });
      }

      if (!foundOpenTrade) {
        const storedTradeId = localStorage.getItem('manualTradingActiveTradeId');
        if (storedTradeId) {
          localStorage.removeItem('manualTradingActiveTradeId');
          localStorage.removeItem('manualTradingActiveInstrument');
          localStorage.removeItem('manualTradingActiveResolution');
          localStorage.removeItem('manualTradingIsActiveTradeManual');
          setCurrentTradeId(null);
          setActiveTradeInstrument('');
          setActiveTradeResolution('');
          setIsActiveTradeManual(false);
        }
      }
    } catch (error) {
      console.error('Error loading open trade status:', error);
    }
  };

  const handleOpenTradeWithData = async (
    instrument: string,
    resolution: string,
    tradeSize: number,
    reason: string,
    direction: 'LONG' | 'SHORT'
  ) => {
    if (!instrument || !resolution || !reason) {
      setModalTitle('Validation Error');
      setModalVariant('error');
      setModalContent(<div className="error-message">Please select instrument, resolution, and entry reason</div>);
      setShowModal(true);
      return;
    }

    setLoading(true);
    const tradeId = `manual_${Date.now()}`;

    try {
      const response = await manualTradingApi.sendSignal({
        signal_type: direction,
        instrument: instrument,
        timeframe: resolution,
        entry_price: 0,
        size: tradeSize,
        entry_reason: reason,
        trade_id: tradeId,
      });

      if (response.status === 'success') {
        setCurrentTradeId(tradeId);
        setActiveTradeInstrument(instrument);
        setActiveTradeResolution(resolution);
        setIsActiveTradeManual(true);

        localStorage.setItem('manualTradingActiveTradeId', tradeId);
        localStorage.setItem('manualTradingActiveInstrument', instrument);
        localStorage.setItem('manualTradingActiveResolution', resolution);
        localStorage.setItem('manualTradingIsActiveTradeManual', 'true');

        const currentTime = Math.floor(Date.now() / 1000);
        const latestPrice = response.entry_price || 0;

        setEntryPrice(latestPrice);
        setEntryTime(currentTime);

        setModalTitle('Trade Opened Successfully');
        setModalVariant('success');
        setModalContent(
          <div className="success-message">
            <p><strong>Direction:</strong> {direction}</p>
            <p><strong>Instrument:</strong> {instrument}</p>
            <p><strong>Size:</strong> {tradeSize}</p>
            <p><strong>Entry Reason:</strong> {reason}</p>
            {response.message && <p>{response.message}</p>}
          </div>
        );
        setShowModal(true);
      } else {
        throw new Error(response.message || 'Failed to open trade');
      }
    } catch (error: any) {
      console.error('Error opening trade:', error);
      setModalTitle('Trade Failed');
      setModalVariant('error');
      setModalContent(<div className="error-message">{error.response?.data?.message || error.message || 'Failed to open trade'}</div>);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTrade = async (reason?: string) => {
    const exitReasonToUse = reason || exitReason;

    if (!currentTradeId || !exitReasonToUse) {
      setModalTitle('Validation Error');
      setModalVariant('error');
      setModalContent(<div className="error-message">No open trade or exit reason not selected</div>);
      setShowModal(true);
      return;
    }

    setLoading(true);

    try {
      let instrumentToUse = activeTradeInstrument;
      let resolutionToUse = activeTradeResolution;

      if (!instrumentToUse || !resolutionToUse || instrumentToUse === 'undefined' || resolutionToUse === 'undefined') {
        const allTrades = await manualTradingApi.getTrades();
        const currentTrade = allTrades?.find((t: any) =>
          t.tradingview_trade_id === currentTradeId && t.status === 'OPEN'
        );

        if (currentTrade) {
          instrumentToUse = currentTrade.instrument;
          resolutionToUse = currentTrade.timeframe;
          setActiveTradeInstrument(instrumentToUse);
          setActiveTradeResolution(resolutionToUse);
          localStorage.setItem('manualTradingActiveInstrument', instrumentToUse);
          localStorage.setItem('manualTradingActiveResolution', resolutionToUse);
        } else {
          throw new Error('Could not find active trade details');
        }
      }

      const response = await manualTradingApi.sendSignal({
        signal_type: 'EXIT',
        instrument: instrumentToUse,
        timeframe: resolutionToUse,
        entry_price: 0,
        exit_price: 0,
        size: size,
        exit_reason: exitReasonToUse,
        trade_id: currentTradeId,
      });

      if (response.status === 'success' || response.status === 'warning') {
        localStorage.removeItem('manualTradingActiveTradeId');
        localStorage.removeItem('manualTradingActiveInstrument');
        localStorage.removeItem('manualTradingActiveResolution');
        localStorage.removeItem('manualTradingIsActiveTradeManual');
        setCurrentTradeId(null);
        setActiveTradeInstrument('');
        setActiveTradeResolution('');
        setIsActiveTradeManual(false);
        setExitReason('');
        setModalTitle('Trade Closed');
        setModalVariant(response.status === 'warning' ? 'warning' : 'success');
        setModalContent(<div className="success-message">{response.message}</div>);
        setShowModal(true);
      } else {
        throw new Error(response.message || 'Failed to close trade');
      }
    } catch (error: any) {
      console.error('Error closing trade:', error);
      setModalTitle('Close Trade Failed');
      setModalVariant('error');
      setModalContent(<div className="error-message">{error.response?.data?.message || error.message || 'Failed to close trade'}</div>);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTradeModalSubmit = async (data: {
    instrument: string;
    resolution: string;
    size: number;
    entryReason?: string;
    exitReason?: string;
    tradeId?: string;
  }) => {
    if (tradeModalType === 'EXIT') {
      if (data.tradeId) {
        setCurrentTradeId(data.tradeId);
        setActiveTradeInstrument(data.instrument);
        setActiveTradeResolution(data.resolution);
        localStorage.setItem('manualTradingActiveTradeId', data.tradeId);
        localStorage.setItem('manualTradingActiveInstrument', data.instrument);
        localStorage.setItem('manualTradingActiveResolution', data.resolution);
      }
      setExitReason(data.exitReason || '');
      await handleCloseTrade(data.exitReason);
    } else {
      setSize(data.size);
      const direction = tradeModalType === 'BUY' ? 'LONG' : 'SHORT';
      await handleOpenTradeWithData(data.instrument, data.resolution, data.size, data.entryReason || '', direction);
    }
    setShowTradeModal(false);
  };

  const openTradeModal = async (type: 'BUY' | 'SELL' | 'EXIT') => {
    setTradeModalType(type);

    if (type === 'EXIT') {
      try {
        const allTrades = await manualTradingApi.getTrades();
        const activeTrades = allTrades.filter((t: any) => t.status === 'OPEN');
        setOpenTrades(activeTrades);
      } catch (error) {
        console.error('Failed to load open trades:', error);
        setOpenTrades([]);
      }
    }

    setShowTradeModal(true);
  };

  // ML Prediction handlers
  const handleGetH1MfePrediction = async () => {
    setMlLoading(true);
    setH1MfePrediction(null);
    try {
      const result = await mlApi.getH1MfePrediction();
      setH1MfePrediction(result);
    } catch (error: any) {
      console.error('Error getting H1 MFE prediction:', error);
      setH1MfePrediction({ success: false, error: error.message || 'Failed to get prediction' });
    } finally {
      setMlLoading(false);
    }
  };

  const handleGetReversalPrediction = async () => {
    setMlLoading(true);
    setReversalPrediction(null);
    try {
      const result = await mlApi.getReversalPrediction({
        direction: 'BUY',
        entryPrice: 0,
        timeInTradeMinutes: 60
      });
      setReversalPrediction(result);
    } catch (error: any) {
      console.error('Error getting reversal prediction:', error);
      setReversalPrediction({ success: false, error: error.message || 'Failed to get prediction' });
    } finally {
      setMlLoading(false);
    }
  };

  return (
    <div className="manual-trading-page">
      <div className="page-header">
        <h2>Manual Trading</h2>
        <p className="page-description">Place and manage manual trades</p>
      </div>

      <div className="chart-card">
        <div className="chart-header">
          <div className="chart-header-left">
            <h3>Console</h3>
            {currentTradeId && (
              <div className="active-trade-badge">
                <div className="trade-badge-indicator"></div>
                <div className="trade-badge-content">
                  <span className="trade-badge-label">Active Trade</span>
                  <span className="trade-badge-details">
                    #{currentTradeId} • ${entryPrice?.toFixed(4)} • {new Date(entryTime! * 1000).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="chart-header-right">
            <div className="trade-actions">
              <button
                className={`trade-action-btn trade-action-buy ${(!!currentTradeId && isActiveTradeManual) || loading ? 'disabled' : ''}`}
                onClick={() => openTradeModal('BUY')}
                disabled={(!!currentTradeId && isActiveTradeManual) || loading}
              >
                Buy
              </button>
              <button
                className={`trade-action-btn trade-action-sell ${(!!currentTradeId && isActiveTradeManual) || loading ? 'disabled' : ''}`}
                onClick={() => openTradeModal('SELL')}
                disabled={(!!currentTradeId && isActiveTradeManual) || loading}
              >
                Sell
              </button>
              <button
                className={`trade-action-btn trade-action-exit ${!currentTradeId || loading ? 'disabled' : ''}`}
                onClick={() => openTradeModal('EXIT')}
                disabled={!currentTradeId || loading}
              >
                Exit
              </button>
              <div className="ml-prediction-buttons">
                <button
                  className={`trade-action-btn ml-prediction-btn ${mlLoading ? 'disabled' : ''}`}
                  onClick={handleGetH1MfePrediction}
                  disabled={mlLoading}
                  title="Check H1 MFE ML Entry Prediction"
                >
                  {mlLoading ? '...' : 'H1 MFE'}
                </button>
                <button
                  className={`trade-action-btn ml-prediction-btn ${mlLoading ? 'disabled' : ''}`}
                  onClick={handleGetReversalPrediction}
                  disabled={mlLoading}
                  title="Check M15 Reversal Prediction"
                >
                  {mlLoading ? '...' : 'Reversal'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ML Prediction Results */}
        {(h1MfePrediction || reversalPrediction) && (
          <div className="ml-prediction-results">
            {h1MfePrediction && (
              <div className={`ml-prediction-card ${h1MfePrediction.success ? (h1MfePrediction.wouldEnter ? 'signal-enter' : 'signal-no') : 'signal-error'}`}>
                <div className="ml-prediction-header">
                  <h4>H1 MFE ML Entry</h4>
                  <button className="close-btn" onClick={() => setH1MfePrediction(null)}>&times;</button>
                </div>
                {h1MfePrediction.success ? (
                  <div className="ml-prediction-content">
                    <div className="prediction-row">
                      <span className="label">Should Enter:</span>
                      <span className={`value ${h1MfePrediction.prediction?.shouldEnter ? 'positive' : 'negative'}`}>
                        {h1MfePrediction.prediction?.shouldEnter ? 'YES' : 'NO'}
                      </span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Direction:</span>
                      <span className={`value ${h1MfePrediction.prediction?.direction === 'BUY' ? 'positive' : 'negative'}`}>
                        {h1MfePrediction.prediction?.direction || 'N/A'}
                      </span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Confidence:</span>
                      <span className="value">{((h1MfePrediction.prediction?.confidence || 0) * 100).toFixed(1)}%</span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Expected MFE:</span>
                      <span className="value">${h1MfePrediction.prediction?.expectedMfeDollars?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">ADX:</span>
                      <span className={`value ${h1MfePrediction.filters?.adxPassed ? 'positive' : 'negative'}`}>
                        {h1MfePrediction.filters?.adxValue?.toFixed(1) || 'N/A'}
                      </span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Would Enter:</span>
                      <span className={`value ${h1MfePrediction.wouldEnter ? 'positive' : 'negative'}`}>
                        {h1MfePrediction.wouldEnter ? 'YES' : 'NO'}
                      </span>
                    </div>
                    {h1MfePrediction.blockReason && (
                      <div className="prediction-row block-reason">
                        <span className="label">Block Reason:</span>
                        <span className="value negative">{h1MfePrediction.blockReason}</span>
                      </div>
                    )}
                    <div className="prediction-row">
                      <span className="label">Price:</span>
                      <span className="value">${h1MfePrediction.indicators?.price?.toFixed(4) || 'N/A'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="ml-prediction-error">{h1MfePrediction.error || 'Failed to get prediction'}</div>
                )}
              </div>
            )}

            {reversalPrediction && (
              <div className={`ml-prediction-card ${reversalPrediction.success ? (reversalPrediction.wouldExit ? 'signal-exit' : 'signal-hold') : 'signal-error'}`}>
                <div className="ml-prediction-header">
                  <h4>M15 Reversal</h4>
                  <button className="close-btn" onClick={() => setReversalPrediction(null)}>&times;</button>
                </div>
                {reversalPrediction.success ? (
                  <div className="ml-prediction-content">
                    <div className="prediction-row">
                      <span className="label">Reversal Prob:</span>
                      <span className={`value ${reversalPrediction.prediction?.probabilityReversal >= 0.5 ? 'negative' : 'positive'}`}>
                        {((reversalPrediction.prediction?.probabilityReversal || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Threshold:</span>
                      <span className="value">{((reversalPrediction.prediction?.threshold || 0) * 100).toFixed(0)}%</span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Would Exit:</span>
                      <span className={`value ${reversalPrediction.wouldExit ? 'negative' : 'positive'}`}>
                        {reversalPrediction.wouldExit ? 'YES (EXIT)' : 'NO (HOLD)'}
                      </span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">Current Price:</span>
                      <span className="value">${reversalPrediction.indicators?.price?.toFixed(4) || 'N/A'}</span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">RSI:</span>
                      <span className="value">{reversalPrediction.indicators?.rsi?.toFixed(1) || 'N/A'}</span>
                    </div>
                    <div className="prediction-row">
                      <span className="label">ADX:</span>
                      <span className="value">{reversalPrediction.indicators?.adx?.toFixed(1) || 'N/A'}</span>
                    </div>
                    {reversalPrediction.exitReason && (
                      <div className="prediction-row exit-reason">
                        <span className="label">Exit Reason:</span>
                        <span className="value negative">{reversalPrediction.exitReason}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ml-prediction-error">{reversalPrediction.error || 'Failed to get prediction'}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Console Content */}
        <div className="console-wrapper">
          <ConsoleLog
            logs={consoleLogs}
            maxLogs={500}
            onClear={() => setConsoleLogs([])}
          />
        </div>
      </div>

      <TradeModal
        isOpen={showTradeModal}
        onClose={() => setShowTradeModal(false)}
        type={tradeModalType}
        instruments={instruments}
        resolutions={resolutions}
        entryReasons={entryReasons}
        exitReasons={exitReasons}
        openTrades={openTrades}
        onSubmit={handleTradeModalSubmit}
        loading={loading}
      />

      <ResultModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        variant={modalVariant}
      >
        {modalContent}
      </ResultModal>
    </div>
  );
};

export default ManualTrading;
